import React, { useState, useEffect } from 'react';
import hawaiian from '../assets/customers/hawaiian.png';
import ita from '../assets/customers/ita.png';
import premia from '../assets/customers/premia.png';
import canada from '../assets/customers/canada.png';
import alaska from '../assets/customers/alaska.png';
import airfrance from '../assets/customers/airfrance.png';
import ba from '../assets/customers/ba.png';
import qatar from '../assets/customers/qatar.png';
import airindia from '../assets/customers/airindia.png';
import sas from '../assets/customers/sas.png';
import jetblue from '../assets/customers/jetblue.png';

interface LogoTickerProps {
  className?: string;
}

const LogoTicker: React.FC<LogoTickerProps> = ({ className }) => {
  // State to track if we're on mobile
  const [isMobile, setIsMobile] = useState(false);

  // Check window size on mount and resize
  useEffect(() => {
    const checkIfMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    // Initial check
    checkIfMobile();

    // Add resize listener
    window.addEventListener('resize', checkIfMobile);

    // Cleanup
    return () => window.removeEventListener('resize', checkIfMobile);
  }, []);

  // Organize logos into specific rows
  const firstRowLogos = [
    { src: hawaiian, alt: 'Hawaiian Airlines' },
    { src: ita, alt: 'ITA Airways' },
    { src: premia, alt: 'Air Premia' },
    { src: canada, alt: 'Air Canada' },
  ];

  const secondRowLogos = [
    { src: alaska, alt: 'Alaska Airlines' },
    { src: ba, alt: 'British Airways' },
    { src: sas, alt: 'SAS' },
    { src: jetblue, alt: 'JetBlue' },
  ];

  const thirdRowLogos = [
    { src: airfrance, alt: 'Air France' },
    { src: airindia, alt: 'Air India' },
    { src: qatar, alt: 'Qatar Airways' },
  ];

  // Mobile view - 2-column grid with all logos
  if (isMobile) {
    const allLogos = [...firstRowLogos, ...secondRowLogos, ...thirdRowLogos];
    return (
      <div style={{
        padding: '20px',
        backgroundColor: 'var(--pure-white)',
        display: 'grid',
        gridTemplateColumns: 'repeat(2, 1fr)',
        gap: '20px'
      }}>
        {allLogos.map((logo, index) => (
          <div key={index} style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            backgroundColor: 'transparent',
            padding: '10px',
            height: '120px'
          }}>
            <img
              src={logo.src}
              alt={logo.alt}
              style={{
                height: '80px',
                width: 'auto',
                objectFit: 'contain',
                filter: 'brightness(1) contrast(1)',
                transition: 'all 0.3s ease'
              }}
            />
          </div>
        ))}
      </div>
    );
  }

  // Desktop view - custom layout: 2 rows of 4, then 1 row of 3
  return (
    <div style={{
      padding: '40px 20px',
      backgroundColor: 'var(--pure-white)',
      width: '100%'
    }}>
      {/* First row - 4 logos */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(4, 1fr)',
        gap: '30px',
        marginBottom: '30px',
        maxWidth: '1000px',
        margin: '0 auto 30px auto'
      }}>
        {firstRowLogos.map((logo, index) => (
          <div key={`row1-${index}`} style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            backgroundColor: 'transparent',
            padding: '20px',
            height: '140px',
            transition: 'transform 0.3s ease',
            cursor: 'pointer'
          }}
            onMouseOver={(e) => {
              e.currentTarget.style.transform = 'scale(1.05)';
            }}
            onMouseOut={(e) => {
              e.currentTarget.style.transform = 'scale(1)';
            }}>
            <img
              src={logo.src}
              alt={logo.alt}
              style={{
                height: '90px',
                width: 'auto',
                objectFit: 'contain',
                filter: 'brightness(1) contrast(1)',
                transition: 'all 0.3s ease'
              }}
            />
          </div>
        ))}
      </div>

      {/* Second row - 4 logos */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(4, 1fr)',
        gap: '30px',
        marginBottom: '30px',
        maxWidth: '1000px',
        margin: '0 auto 30px auto'
      }}>
        {secondRowLogos.map((logo, index) => (
          <div key={`row2-${index}`} style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            backgroundColor: 'transparent',
            padding: '20px',
            height: '140px',
            transition: 'transform 0.3s ease',
            cursor: 'pointer'
          }}
            onMouseOver={(e) => {
              e.currentTarget.style.transform = 'scale(1.05)';
            }}
            onMouseOut={(e) => {
              e.currentTarget.style.transform = 'scale(1)';
            }}>
            <img
              src={logo.src}
              alt={logo.alt}
              style={{
                height: '90px',
                width: 'auto',
                objectFit: 'contain',
                filter: 'brightness(1) contrast(1)',
                transition: 'all 0.3s ease'
              }}
            />
          </div>
        ))}
      </div>

      {/* Third row - 3 logos (Air France, Air India, Qatar) */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(3, 1fr)',
        gap: '40px',
        maxWidth: '750px',
        margin: '0 auto'
      }}>
        {thirdRowLogos.map((logo, index) => (
          <div key={`row3-${index}`} style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            backgroundColor: 'transparent',
            padding: '20px',
            height: '140px',
            transition: 'transform 0.3s ease',
            cursor: 'pointer'
          }}
            onMouseOver={(e) => {
              e.currentTarget.style.transform = 'scale(1.05)';
            }}
            onMouseOut={(e) => {
              e.currentTarget.style.transform = 'scale(1)';
            }}>
            <img
              src={logo.src}
              alt={logo.alt}
              style={{
                height: '90px',
                width: 'auto',
                objectFit: 'contain',
                filter: 'brightness(1) contrast(1)',
                transition: 'all 0.3s ease'
              }}
            />
          </div>
        ))}
      </div>
    </div>
  );
};

export default LogoTicker; 